import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EnvService } from '@lib/env';
import { UriBuilder } from 'uribuilder';
import { Observable } from 'rxjs';
import { IPurchaseRequisition, PurchaseRequisition } from './types/PurchaseRequisition';
import { map, switchMap } from 'rxjs/operators';
import { DemandDataService } from '../demand/demand-data.service';
import { IPurchaseRequisitionPreview, PurchaseRequisitionPreview } from './types/IPurchaseRequisitionPreview';
import { HttpExtras, IPaginationSortingResponse, Sorting, UserPagination } from '@lib/core';
import { PurchaseRequisitionHistoryEntry } from './types/PurchaseRequisitionHistoryEntry';
import { IPurchaseRequisitionHistoryEntry } from './types/IPurchaseRequisitionHistoryEntry';
import { IUser, PersonExtended, UserService } from '@lib/user';
import { PurchaseRequisitionListFilter } from './types/PurchaseRequisitionListFilter';
import { PurchaseRequisitionSorting } from './types/PurchaseRequisitionSorting';
import { CommentDataService } from '@lib/comment';
import { ItemClassificationDataService } from '@lib/classification';
import { FileDataService } from '@lib/file';
import { I18nService } from '@lib/i18n';
import { IPurchaseRequisitionCompanyCodeValidationResponse } from './types/IPurchaseRequisitionCompanyCodeValidationResponse';
import { ISelectableConsumer, selectableConsumerToPerson } from './types/ISelectableConsumer';
import { CustomerDepartment, IDepartment } from '@lib/client';
import { OrderByPipe, SortingService } from '@lib/util';

@Injectable({
    providedIn: 'root',
})
export class PurchaseRequisitionDataService {
    private readonly http = inject(HttpClient);
    private readonly env = inject(EnvService);
    private readonly demandDataService = inject(DemandDataService);
    private readonly commentDataService = inject(CommentDataService);
    private readonly itemClassificationDataService = inject(ItemClassificationDataService);
    private readonly fileDataService = inject(FileDataService);
    private readonly userService = inject(UserService);
    private readonly i18n = inject(I18nService);

    public findOne(id: string): Observable<PurchaseRequisition> {
        return this.http
            .get<IPurchaseRequisition>(this.getBase(id).toString(), { params: HttpExtras.IGNORE_CACHE })
            .pipe(
                map(
                    (p) =>
                        new PurchaseRequisition(
                            p,
                            this.demandDataService,
                            this.commentDataService,
                            this.itemClassificationDataService,
                            this.fileDataService,
                            this.i18n,
                        ),
                ),
            );
    }

    public withdraw(id: string): Observable<void> {
        return this.http.put<void>(this.getBase(id, 'withdraw').toString(), {});
    }

    public findAll(
        filter: PurchaseRequisitionListFilter,
        pagination: UserPagination,
        sorting: Sorting<PurchaseRequisitionSorting>,
    ): Observable<IPaginationSortingResponse<PurchaseRequisitionPreview, any>> {
        const uri = this.getBase();
        uri.query = {
            sortingColumn: sorting.column,
            sortingDirection: sorting.direction,
        };
        pagination.applyToUri(uri);
        filter.apply(uri);
        return this.http
            .get<IPaginationSortingResponse<IPurchaseRequisitionPreview, any>>(uri.toString(), {
                params: HttpExtras.IGNORE_CACHE,
            })
            .pipe(
                map((result) => ({
                    pagination: result.pagination,
                    sorting: result.sorting,
                    data: result.data.map((preview) => PurchaseRequisitionPreview.fromDto(preview)),
                })),
            );
    }

    public countByFilter(filter: PurchaseRequisitionListFilter): Observable<number> {
        const uri = this.getBase('count');
        filter.apply(uri);

        return this.http.get<number>(uri.toString(), { params: HttpExtras.IGNORE_CACHE });
    }

    public getHistory(purchaseRequisitionId: string): Observable<PurchaseRequisitionHistoryEntry[]> {
        return this.http
            .get<IPurchaseRequisitionHistoryEntry[]>(this.getBase(purchaseRequisitionId, 'history').toString(), {
                params: HttpExtras.IGNORE_CACHE,
            })
            .pipe(
                switchMap(async (history) => {
                    const user: IUser = await this.userService.getMyself();
                    return history.map((entry) => new PurchaseRequisitionHistoryEntry(entry, user.id, this.i18n));
                }),
            );
    }

    public validateCompanyCode(
        purchaseRequisitionId: string,
    ): Observable<IPurchaseRequisitionCompanyCodeValidationResponse> {
        const uri = this.getBase(purchaseRequisitionId, 'extensions', 'accounting', 'validate-company-code');

        return this.http.get<IPurchaseRequisitionCompanyCodeValidationResponse>(uri.toString(), {
            params: HttpExtras.IGNORE_CACHE,
        });
    }

    /**
     * Requesters that appear in at least one purchase requisition the user may see — the option set
     * of the "Bedarfsträger" filter. Derived server-side from the same visibility predicate as the
     * list, so no option yields an empty result (EPROC-11749).
     */
    public findSelectableConsumers(): Observable<PersonExtended[]> {
        return this.http
            .get<ISelectableConsumer[]>(this.getBase('consumers', 'selectable').toString(), {
                params: HttpExtras.IGNORE_CACHE,
            })
            .pipe(
                map((consumers: ISelectableConsumer[]) =>
                    OrderByPipe.sortObjectsByStringValue(consumers.map(selectableConsumerToPerson), 'fullName'),
                ),
            );
    }

    /**
     * Departments that appear in at least one purchase requisition the user may see — the option set
     * of the "Abteilung" filter (EPROC-11749).
     */
    public findSelectableDepartments(): Observable<CustomerDepartment[]> {
        return this.http
            .get<IDepartment[]>(this.getBase('departments', 'selectable').toString(), {
                params: HttpExtras.IGNORE_CACHE,
            })
            .pipe(
                map((departments: IDepartment[]) =>
                    SortingService.sort(
                        departments.map((dto) => CustomerDepartment.from(dto)),
                        (d) => d.valueDisplay,
                    ),
                ),
            );
    }

    private getBase(...pathSegments: (string | number)[]): UriBuilder {
        return this.env.getBase('purchase-requisition', ...pathSegments);
    }
}
